import { apiCallPost } from '../api';
import { apiPaths } from '../config/apiPaths';
import { buildMessageContent } from '../../utils/messageContentBuilder';

const callConversationApi = async (operation, payload, authHeaders) => {
  try {
    const requestPayload = {
      operation,
      payload,
    };

    const res = await apiCallPost(apiPaths.getConversation, requestPayload, authHeaders);

    if (res) {
      return {
        status: true,
        data: res,
      };
    }

    return {
      status: false,
      errMsg: res?.msg || 'Invalid response from server',
    };
  } catch (err) {
    return {
      status: false,
      errMsg: err?.message || `Error with operation: ${operation}`,
    };
  }
};

export const getConversationApi = async (inputData, authHeaders) => {
  const { conversationId } = inputData;

  if (!conversationId) {
    return {
      status: false,
      errMsg: 'Conversation ID is required',
    };
  }

  const res = await callConversationApi(
    'GET_CONVERSATION',
    { conversationId },
    authHeaders
  );

  return {
    ...res,
    msg: res.status ? 'Conversation fetched successfully' : res.errMsg,
  };
};

export const listConversationsApi = async (inputData = {}, authHeaders) => {
  const { status = 'OPEN', limit = 10, nextKey = null } = inputData;

  const payload = {
    status,
    limit,
    ...(nextKey ? { nextKey } : {}),
  };

  const res = await callConversationApi(
    'LIST_CONVERSATIONS',
    payload,
    authHeaders
  );

  return {
    ...res,
    msg: res.status ? 'Conversations fetched successfully' : res.errMsg,
  };
};

export const getConversationsApi = async (inputData, authHeaders) => {
  const { conversationIds = [] } = inputData;

  if (!conversationIds.length) {
    return {
      status: false,
      errMsg: 'At least one conversation ID is required',
    };
  }

  try {
    const requests = conversationIds.map((id) =>
      getConversationApi({ conversationId: id }, authHeaders)
    );
    const results = await Promise.all(requests);
    const allSuccessful = results.every((r) => r.status === true);

    if (!allSuccessful) {
      const failedRequests = results.filter((r) => r.status === false);
      return {
        status: false,
        errMsg: `Failed to fetch some conversations: ${failedRequests.map((r) => r.errMsg).join(', ')}`,
      };
    }

    return {
      status: true,
      data: results.map((r) => r.data),
      msg: 'Conversations fetched successfully',
    };
  } catch (err) {
    return {
      status: false,
      errMsg: err?.message || 'Error fetching conversations',
    };
  }
};

export const sendMessageApi = async (inputData, authHeaders) => {
  const { conversationId, contentType = 'TEXT', contentData = {} } = inputData;

  if (!conversationId) {
    return {
      status: false,
      errMsg: 'Conversation ID is required',
    };
  }

  if (!contentType) {
    return {
      status: false,
      errMsg: 'Content type is required',
    };
  }

  try {
    const content = buildMessageContent(contentType, contentData);

    const res = await callConversationApi(
      'SEND_MESSAGE',
      { conversationId, content },
      authHeaders
    );

    return {
      ...res,
      msg: res.status ? 'Message sent successfully' : res.errMsg,
    };
  } catch (contentError) {
    return {
      status: false,
      errMsg: contentError.message,
    };
  }
};

export const closeConversationApi = async (inputData, authHeaders) => {
  const { conversationId } = inputData;

  if (!conversationId) {
    return {
      status: false,
      errMsg: 'Conversation ID is required',
    };
  }

  const res = await callConversationApi(
    'CLOSE_CONVERSATION',
    { conversationId },
    authHeaders
  );

  return {
    ...res,
    msg: res.status ? 'Conversation closed successfully' : res.errMsg,
  };
};

